import { useEffect, useState } from 'react';
import config from '../config';

const conusCenter = [-89.5, 35];

const baseZoomPoint = {
  geometry: { coordinates: conusCenter }
};

const baseAsset = {
  asset: {
    name: 'assets',
    sourceInfo: {
      type: 'geojson',
      data: null
    },
    layerInfo: {
      id: 'assets',
      source: 'assets',
      type: 'line',
      paint: {
        'line-width': 5,
        'line-color': 'blue'
      }
    }
  },
  river: {
    name: 'river',
    sourceInfo: {
      type: 'geojson',
      data: null
    },
    layerInfo: {
      id: 'river',
      source: 'river',
      type: 'line',
      paint: {
        'line-width': 2,
        'line-color': '#38c6f5'
      }
    }
  },
  xs: {
    name: 'xs',
    sourceInfo: {
      type: 'geojson',
      data: null
    },
    layerInfo: {
      id: 'xs',
      source: 'xs',
      type: 'line',
      paint: {
        'line-width': 2,
        'line-color': 'red'
      }
    }
  },
  center: conusCenter,
  zoom: 14
};

export default function useSelectedAsset({
  stateSchema,
  locationIDs,
  handleLocationIDsChange,
  riverID,
  handleRiverIDChange,
}) {
  const [selectedAsset, setSelectedAsset] = useState(baseAsset);
  const [assetGeometryData, setAssetGeometryData] = useState(null);
  const [assetZoomPoint, setAssetZoomPoint] = useState(baseZoomPoint);

  const [xsGeometryData, setXsGeometryData] = useState(null);
  const [riverGeometryData, setRivertGeometryData] = useState(null);

  const [assetLoaded, setAssetLoaded] = useState(false);
  const [assetGeomLoaded, setAssetGeomLoaded] = useState(false);
  const [assetZoomLoaded, setAssetZoomLoaded] = useState(false);
  const [riverIDLoaded, setRiverIDLoaded] = useState(false);
  const [xsGeomLoaded, setXsGeomLoaded] = useState(false);
  const [riverGeomLoaded, setRiverGeomLoaded] = useState(false);

  useEffect(() => {
    setAssetZoomLoaded(false);
    setAssetGeomLoaded(false);
    setRiverIDLoaded(false);
    if (stateSchema && locationIDs) {
      // get the center point of the map
      fetch(
        `${config.featureServer}/collections/${stateSchema}.triggers/items/${locationIDs.trigger_id}?properties=asset_id`,
        { method: 'GET' }
      )
        .then((r) => {
          if (r.status !== 200) {
            handleLocationIDsChange(undefined);
            throw new Error('Invalid response from feature server');
          }
          return r;
        })
        .then((res) => res.json())
        .catch((error) => alert('Error! ' + error.message))
        .then(setAssetZoomPoint)
        .then(() => setAssetZoomLoaded(true));

      // get the asset geometry
      fetch(
        `${config.featureServer}/collections/${stateSchema}.assets/items/${locationIDs.asset_id}?properties=asset_id`,
        { method: 'GET' }
      )
        .then((r) => {
          if (r.status !== 200) {
            handleLocationIDsChange(undefined);
            throw new Error('Invalid response from feature server');
          }
          return r;
        })
        .then((res) => res.json())
        .catch((error) => alert('Error! ' + error.message))
        .then(setAssetGeometryData)
        .then(() => setAssetGeomLoaded(true));

      // get the river_id from the api
      fetch(`${config.apiURL}/river/${stateSchema}/${locationIDs.xs_id}`, {
        method: 'GET'
      })
        .then((r) => {
          if (r.status !== 200) {
            handleLocationIDsChange(undefined);
            throw new Error('Invalid response from api');
          }
          return r;
        })
        .catch((error) => alert('Error! ' + error.message))
        .then((res) => (res ? res.json() : undefined))
        .then((r) => (r ? handleRiverIDChange(r.river_id) : undefined))
        .then(() => setRiverIDLoaded(true));
    } else {
      handleRiverIDChange(undefined);
      setAssetZoomPoint(baseZoomPoint);
      setAssetGeometryData(null);
      setSelectedAsset(baseAsset);
    }
    // eslint-disable-next-line
  }, [stateSchema, locationIDs]);

  // use the river_id to get the xs geoms
  useEffect(() => {
    setXsGeomLoaded(false);
    if (stateSchema && riverID) {
      fetch(
        `${config.featureServer}/collections/${stateSchema}.xs/items?properties=river_id,xs_id,channel_burn&river_id=${riverID}`,
        { method: 'GET' }
      )
        .then((r) => {
          if (r.status !== 200) {
            handleLocationIDsChange(undefined);
            throw new Error('Invalid response from feature server');
          }
          return r;
        })
        .then((res) => res.json())
        .catch((error) => alert('Error! ' + error.message))
        .then(setXsGeometryData)
        .then(() => setXsGeomLoaded(true));
    } else {
      handleRiverIDChange(undefined);
      setXsGeometryData(null);
    }
    // eslint-disable-next-line
  }, [stateSchema, riverID]);

  // use the river_id to get the river geoms
  useEffect(() => {
    setRiverGeomLoaded(false);
    if (stateSchema && riverID) {
      fetch(
        `${config.featureServer}/collections/${stateSchema}.rivers/items/${riverID}?properties=river_id`,
        { method: 'GET' }
      )
        .then((r) => {
          if (r.status !== 200) {
            handleLocationIDsChange(undefined);
            throw new Error('Invalid response from feature server');
          }
          return r;
        })
        .then((res) => res.json())
        .catch((error) => alert('Error! ' + error.message))
        .then(setRivertGeometryData)
        .then(() => setRiverGeomLoaded(true));
    } else {
      handleRiverIDChange(undefined);
      setRivertGeometryData(null);
    }
    // eslint-disable-next-line
  }, [stateSchema, riverID]);

  // wait until all requests are finished to avoid map flickering
  // this could use a loading spinning wheel at some point
  useEffect(() => {
    if (
      assetGeomLoaded &&
      assetZoomLoaded &&
      riverIDLoaded &&
      xsGeomLoaded &&
      riverGeomLoaded
    ) {
      setAssetLoaded(true);
    } else {
      setAssetLoaded(false);
    }
  }, [
    assetGeomLoaded,
    assetZoomLoaded,
    riverIDLoaded,
    xsGeomLoaded,
    riverGeomLoaded
  ]);

  // once the data has all be loaded, return the new asset as an object
  // to rerender the map with the new data
  useEffect(() => {
    if (assetLoaded) {
      const newAsset = {
        asset: {
          name: 'assets',
          sourceInfo: {
            type: 'geojson',
            data: assetGeometryData
          },
          layerInfo: {
            id: 'assets',
            source: 'assets',
            type: 'line',
            paint: {
              'line-width': 5,
              'line-color': 'magenta'
            }
          }
        },
        river: {
          name: 'River',
          sourceInfo: {
            type: 'geojson',
            data: riverGeometryData
          },
          layerInfo: {
            id: 'River',
            source: 'River',
            type: 'line',
            paint: {
              'line-width': 2,
              'line-color': '#38c6f5'
            },
            layout: { visibility: 'none' } // start with all layers turned off
          }
        },
        xs: {
          name: 'XS',
          sourceInfo: {
            type: 'geojson',
            data: xsGeometryData
          },
          layerInfo: {
            id: 'XS',
            source: 'XS',
            type: 'line',
            paint: {
              'line-width': 2,
              'line-color': 'green'
            },
            layout: { visibility: 'none' } // start with all layers turned off
          }
        },
        center: assetZoomPoint
          ? assetZoomPoint.geometry.coordinates
          : conusCenter,
        zoom: assetGeometryData && assetZoomPoint ? 12 : 3
      };
      setSelectedAsset(newAsset);
    }
    // eslint-disable-next-line
  }, [assetLoaded]);
  // }, [assetLoaded, locationIDs]);

  return { selectedAsset, assetLoaded };
}
